import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAuthStatus } from "../../api";

interface Props {
  /** Called with the latest connection status on each check (mount + polls),
   *  so a host (e.g. the LME send modal) can gate its SEND button. */
  onStatus?: (connected: boolean) => void;
}

/**
 * LME-4(a): in-SPA Microsoft reconnect.
 *
 * Renders nothing while Microsoft is connected. When disconnected it shows a
 * banner + button that opens the Access-gated OAuth start URL in a NEW TAB
 * (window.open — `/api/auth/microsoft/start` is a 302 the SPA can't fetch),
 * then polls `/api/auth/status` until the connection comes back and stops.
 *
 * Conservative: a transient status-fetch error never flips the UI to
 * "disconnected" (only an explicit `microsoft: false` shows the banner), so a
 * blip doesn't false-alarm mid-send.
 */
export function MicrosoftReconnect({ onStatus }: Props) {
  const [connected, setConnected] = useState<boolean | null>(null); // null = checking
  const [waiting, setWaiting] = useState(false);
  // Ref so a changing onStatus prop doesn't re-create `check` (which would
  // re-fire the mount effect every render).
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const check = useCallback(async (): Promise<boolean | null> => {
    try {
      const s = await fetchAuthStatus();
      setConnected(s.microsoft);
      onStatusRef.current?.(s.microsoft);
      return s.microsoft;
    } catch {
      return null; // transient — leave prior state, don't false-alarm
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // While waiting on the reconnect tab, poll until the connection returns.
  useEffect(() => {
    if (!waiting) return;
    const id = window.setInterval(async () => {
      const ok = await check();
      if (ok) setWaiting(false);
    }, 2500);
    return () => window.clearInterval(id);
  }, [waiting, check]);

  if (connected !== false) return null;

  return (
    <div className="ms-reconnect-banner">
      <span className="ms-reconnect-text">
        ⚠ Microsoft disconnected — sending is unavailable until you reconnect.
      </span>
      <button
        className="ms-reconnect-btn"
        onClick={() => {
          window.open("/api/auth/microsoft/start", "_blank", "noopener");
          setWaiting(true);
        }}
        disabled={waiting}
        title="Opens Microsoft sign-in in a new tab; this updates automatically when you finish."
      >
        {waiting ? "WAITING FOR RECONNECT…" : "↻ RECONNECT MICROSOFT"}
      </button>
    </div>
  );
}
